import { FC, PropsWithoutRef, useEffect, useMemo, useState } from "react"
import { Button, Col, Form, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap"
import { ChevronDown, ChevronUp } from "react-bootstrap-icons"
import BootstrapTable, { ColumnDescription, ExpandRowProps } from "react-bootstrap-table-next"
import filterFactory from "react-bootstrap-table2-filter"
import ToolkitProvider from "react-bootstrap-table2-toolkit"
import { useTranslation } from "react-i18next"
import Flag from "react-world-flags"
import api from "../../api"
import { SearchBar } from "../../components/Tables/Columns"
import { countryNamesInEnglish, dateFormatter } from "../../utils/commonFunctions"

const ExpandedBag: FC<PropsWithoutRef<{ bag: Record<string, any> }>> = ({ bag }) => {
    const { t } = useTranslation()
    const { remarks, agent_detail, parcels } = bag

    const columns = useMemo(
        () => [
            {
                text: t("Shipment ID"),
                dataField: "uuid",
                classes: "highlighted-text"
            },
            {
                text: t("Account ID"),
                dataField: "user",
                classes: "highlighted-text",
                formatter: (cell: Record<string, any>, row: Record<string, any>) => {
                    return cell?.user_pin ? cell?.user_pin : "N/A"
                }
            },
            {
                text: t("Date"),
                dataField: "created_at",
                formatter: (cell: string, row: Record<string, any>) => {
                    let date
                    if (cell && cell?.length > 0) {
                        date = dateFormatter(cell, false, "")
                    }
                    return cell && cell?.length > 0 ? (
                        <>
                            <span className="d-block">{date}</span>
                        </>
                    ) : (
                        "-"
                    )
                }
            },
            {
                text: t("A. Weight & Dimensions"),
                dataField: "a-weight",
                formatter: (cell: any, row: Record<string, any>) => (
                    <>
                        {row.weight_unit && typeof row.weight_value === "number" ? (
                            <>
                                <span className="fw-bold">{row.weight_value}</span>
                                &nbsp;
                                <span>{row.weight_unit}</span>
                            </>
                        ) : (
                            "-"
                        )}

                        <br />
                        {typeof row.dimension_length === "number" &&
                        typeof row.dimension_width === "number" &&
                        typeof row.dimension_height === "number" &&
                        row.dimensions_unit ? (
                            <>
                                {`${row.dimension_length}`}&#x2715;
                                {`${row.dimension_width}`}&#x2715;
                                {`${row.dimension_height}`}&nbsp;
                                {row.dimensions_unit}
                            </>
                        ) : (
                            "-"
                        )}
                    </>
                )
            },
            {
                text: t("Destination"),
                dataField: "ParcelAddress",
                formatter: (_cell: any, row: Record<string, any>) => {
                    const cell = _cell?.code
                    return cell && cell !== "" && countryNamesInEnglish.of(cell) ? (
                        <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={
                                <Tooltip id={`${cell}-tooltip`}>{countryNamesInEnglish.of(cell)}</Tooltip>
                            }>
                            <>
                                <Flag
                                    className="d-inline-block mx-1"
                                    style={{ width: 28, height: 18 }}
                                    code={(cell ?? "").toLowerCase()}
                                />
                                &nbsp;{cell}
                            </>
                        </OverlayTrigger>
                    ) : (
                        "-"
                    )
                }
            },
            {
                text: t("Value"),
                dataField: "amount",
                formatter: (cell: number, row: Record<string, any>) => {
                    return cell && cell > 0 ? `$${cell}` : "-"
                }
            },
            {
                text: t("Type"),
                dataField: "cod",
                formatter: (cell: number, row: Record<string, any>) => {
                    return cell === 1 ? "COD" : "Prepaid"
                }
            },
            {
                text: t("Terms"),
                dataField: "terms",
                formatter: (cell: number, row: Record<string, any>) => {
                    return "DDU"
                }
            },
            {
                text: t("LM AWB"),
                dataField: "new_awb_number",
                formatter: (cell: string, row: Record<string, any>) => {
                    return cell?.length > 0 ? cell : "-"
                }
            },
            {
                text: t("TBA"),
                dataField: "purchase_tracking_number",
                formatter: (cell: string, row: Record<string, any>) => {
                    return cell?.length > 0 ? cell : "-"
                }
            }
        ],
        []
    )

    return (
        <>
            <Row className="px-1 align-items-start">
                <Col>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="clearanceAgentDetails">{t("Clearance agent details")}</Form.Label>
                        <Form.Control
                            value={agent_detail}
                            name={"clearanceAgentDetails"}
                            placeholder="Clearance agent details"
                            disabled
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row className="px-1 justify-content-center align-items-center">
                <Col>
                    <Form.Group className="mb-3 w-100">
                        <Form.Label htmlFor="remarks">{t("Remarks")}</Form.Label>
                        <Form.Control
                            value={remarks}
                            maxLength={500}
                            as="textarea"
                            name={"remarks"}
                            autoComplete="off"
                            placeholder="Remarks..."
                            disabled
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row className="px-1">
                <Col>
                    <div className="main-container modal-gray-bg">
                        <BootstrapTable
                            keyField="id"
                            wrapperClasses="table-wrapper my-3 "
                            headerClasses="header-class font-13"
                            bordered={false}
                            noDataIndication={t("No_Record")}
                            rowClasses="custom-table-row"
                            data={parcels.length ? parcels : []}
                            columns={columns}
                        />
                    </div>
                </Col>
            </Row>
        </>
    )
}

const BagsTable: FC<PropsWithoutRef<{ data: any[] }>> = (props) => {
    const { t } = useTranslation()
    const { data } = props ?? {}

    const columns: ColumnDescription<any, any>[] = [
        {
            text: t("Bag ID"),
            dataField: "bag_id",
            classes: "highlighted-text"
        },
        {
            text: t("Date"),
            dataField: "created_at",
            formatter: (cell: string, row: Record<string, any>) => {
                let date
                if (cell && cell?.length > 0) {
                    date = dateFormatter(cell, false, "")
                }
                return cell && cell?.length > 0 ? (
                    <>
                        <span className="d-block">{date}</span>
                    </>
                ) : (
                    "-"
                )
            }
        },
        {
            text: t("Parcels"),
            dataField: "parcels_count",
            formatter: (cell: any, row: Record<string, any>) => {
                return cell ?? row.parcels?.length ?? 0
            }
        },
        {
            text: t("Type"),
            dataField: "bag_type",
            formatter: (cell, row) =>
                (row.bag_type ?? "").toLowerCase() === "transit" ? (
                    <div className="d-flex flex-row flex-nowrap align-items-center">
                        {cell}
                        <Flag
                            className="d-inline-block mx-1"
                            style={{ width: 28, height: 18 }}
                            code={row.final_destination ?? ""}
                        />
                        {row.final_destination}
                    </div>
                ) : (
                    (cell ?? "-")
                )
        },
        {
            text: t("Clearance Agent Details"),
            dataField: "agent_detail",
            formatter: (cell) => cell ?? "-"
        },
        {
            text: t("Dims"),
            dataField: "dims",
            formatter: (cell: any, row: Record<string, any>) => {
                return `${row?.height || "-"} x ${row?.width || "-"} x ${
                    row?.length || "-"
                } ${row?.dimensions_unit || "inch|cm"}`
            }
        },
        {
            text: t("A.Weight"),
            dataField: "a-weight",
            formatter: (cell: any, row: Record<string, any>) => (
                <>
                    {row.actual_weight_value && typeof row.actual_weight_value === "number" ? (
                        <>
                            <span className="fw-bold">{row.actual_weight_value}</span>
                            &nbsp;
                            <span>{row.weight_unit}</span>
                        </>
                    ) : (
                        "-"
                    )}

                    <br />
                    {typeof row.length === "number" &&
                    typeof row.width === "number" &&
                    typeof row.height === "number" &&
                    row.dimensions_unit ? (
                        <>
                            {`${row.length}`}&#x2715;
                            {`${row.width}`}&#x2715;
                            {`${row.height}`}&nbsp;
                            {row.dimensions_unit}
                        </>
                    ) : (
                        "-"
                    )}
                </>
            )
        },
        {
            text: t("Destination"),
            dataField: "destination",
            formatter: (cell: string, row: Record<string, any>) => {
                return cell && cell !== "" && countryNamesInEnglish.of(cell) ? (
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={<Tooltip id={`${cell}-tooltip`}>{countryNamesInEnglish.of(cell)}</Tooltip>}>
                        <>
                            <Flag
                                className="d-inline-block mx-1"
                                style={{ width: 28, height: 18 }}
                                code={(cell ?? "").toLowerCase()}
                            />
                            &nbsp;{cell}
                        </>
                    </OverlayTrigger>
                ) : (
                    "-"
                )
            }
        },
        {
            text: t("Value"),
            dataField: "bag_invoice_value",
            formatter: (cell: number, row: Record<string, any>) => {
                return cell && cell > 0 ? `$${cell}` : "-"
            }
        }
    ]

    const expandRow: ExpandRowProps<any, number> = useMemo(
        () => ({
            renderer: (row) => <ExpandedBag bag={row} />,
            showExpandColumn: true,
            expandByColumnOnly: true,
            expandHeaderColumnRenderer: () => <></>,
            expandColumnRenderer: ({ expanded }: { expanded: any }) => {
                if (expanded) {
                    return <ChevronUp color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
                }
                return <ChevronDown color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
            },
            onlyOneExpanding: true
        }),
        []
    )

    return (
        <div className="table-responsive">
            {columns && (
                <ToolkitProvider
                    keyField="id"
                    data={data ?? []}
                    columns={columns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}>
                    {(props) => (
                        <div className="table-container my-4 container-fluid">
                            <Row className="search suites-search">
                                <Col xs className="d-flex justify-content-end px-0">
                                    <SearchBar {...props.searchProps} />
                                </Col>
                            </Row>
                            <Row className="table-data my-3 modal-gray-bg">
                                <Col>
                                    <div
                                        style={{
                                            maxHeight: "200px",
                                            overflow: "auto"
                                        }}>
                                        <BootstrapTable
                                            {...props.baseProps}
                                            wrapperClasses="table-wrapper my-3 "
                                            headerClasses="header-class font-13"
                                            bordered={false}
                                            noDataIndication={t("No_Record")}
                                            filter={filterFactory()}
                                            rowClasses="custom-table-row"
                                            expandRow={expandRow}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

const MAWBPalletDetailsModal: FC<
    PropsWithoutRef<{
        show: string
        setShow: Function
        showMessage: Function
        data?: any
    }>
> = (props) => {
    const { show, setShow, data, showMessage } = props
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [palletDetails, setPalletDetails] = useState<any>({})
    const { pallet_id } = data ?? {}
    const { LHBags, remarks } = palletDetails

    useEffect(() => {
        if (!pallet_id) return
        const handleAsyncLogic = async () => {
            setLoading(true)
            try {
                const response = await api.linehaul.palletDetails({
                    palletId: pallet_id
                })
                if (!response?.data?.status || !response.data.payload)
                    throw new Error(response?.data?.message ?? "Unable to get pallet data")
                setPalletDetails(response?.data?.payload ?? {})
                setLoading(false)
            } catch (e: any) {
                setLoading(false)
                showMessage(e.message ?? "Unable to get pallet data", "danger", "Error")
                setShow("")
            }
        }
        handleAsyncLogic()
    }, [pallet_id])

    return (
        <Modal
            size="xl"
            show={["mawbPalletDetails"].includes(show)}
            onHide={() => setShow("")}
            backdrop="static"
            scrollable
            keyboard={false}
            dialogClassName="xxl-modal">
            <Modal.Header closeButton>
                <Modal.Title className="text-uppercase">Pallet # {pallet_id}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="modal-gray-bg">
                {loading ? (
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only"></span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="main-container modal-gray-bg">
                            <BagsTable data={LHBags ?? []} />
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <div className="mb-parcels1">Remarks</div>
                                <div
                                    className={`p-2 bg-white rounded text-truncate ${
                                        remarks ? "" : "text-muted"
                                    }`}>
                                    {remarks ? remarks : "-"}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="modal-gray-bg">
                <Button type="button" className="px-3 bg-primary" variant="primary" onClick={() => setShow("")}>
                    {t("Close")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default MAWBPalletDetailsModal
